import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import firebaseService, { DEMO_MODE } from '../services/FirebaseService';

const HeroSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  textAlign: 'center',
  minHeight: '70vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  background: 'linear-gradient(to bottom, #1a1a1a, #121212)',
}));


const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: '#e94560', // BattleFormation pink/red color
  marginBottom: theme.spacing(2),
}));

const PageContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}));

const AuthCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius,
  background: '#1e1e1e',
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: '#4285F4',
  color: 'white',
  '&:hover': {
    backgroundColor: '#3367D6',
  },
}));

const DemoButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: '#4caf50',
  color: 'white',
  '&:hover': {
    backgroundColor: '#388e3c',
  },
}));

const LandingPage = () => {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();

  // Immediately redirect logged-in users to drafts page
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is signed in, redirect to drafts page
        navigate('/drafts');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  // Check URL parameters for access denied message
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessDenied = urlParams.get('accessDenied');
    
    if (accessDenied === 'true') {
      showSnackbar('Your email has been removed from the allowed list. Please contact the administrator.', 'error');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      await firebaseService.signInWithGoogle();
      // Redirect will happen automatically via the auth state listener
    } catch (error) {
      console.error('Error signing in with Google:', error);
      showSnackbar(error.message, 'error');
    }
  };

  const handleDemoLogin = () => {
    // In demo mode, we navigate directly
    navigate('/draft/new');
  };

  const handleEmailAuth = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await firebaseService.signIn(email, password);
      
      // Close dialog after successful auth
      setShowAuthDialog(false);
      resetForm();
      // Redirect will happen automatically via the auth state listener
    } catch (error) {
      console.error('Authentication error:', error);
      
      let errorMessage = 'Authentication failed';
      
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email format';
          break;
        default:
          errorMessage = error.message;
      }
      
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      
      // Close dialog after showing error in snackbar
      setTimeout(() => {
        setShowAuthDialog(false);
        resetForm();
      }, 1000);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError('');
  };

  const handleDialogClose = () => {
    setShowAuthDialog(false);
    resetForm();
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <PageContainer maxWidth="lg">
      <HeroSection>
        <Logo variant="h2">Dota 2 Draft Planner/Simulator</Logo>
        <Typography variant="h5" gutterBottom color="text.secondary">
          Plan, practice, and perfect your drafting strategy
        </Typography>
        
        <Container maxWidth="sm" sx={{ mt: 6 }}>
          <AuthCard elevation={3}>
            <Typography variant="h5" gutterBottom>
              Get Started
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              {DEMO_MODE ? 'Use the app in demo mode with local file storage' : 'Sign in to start creating and saving drafts'}
            </Typography>
            
            {DEMO_MODE ? (
              <DemoButton
                fullWidth
                variant="contained"
                onClick={handleDemoLogin}
              >
                Start Using App (Local Files Only)
              </DemoButton>
            ) : (
              <>
                <GoogleButton 
                  fullWidth 
                  variant="contained" 
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignIn}
                >
                  Sign in with Google
                </GoogleButton>
                
                <Divider sx={{ my: 2 }}>OR</Divider>
                
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary"
                  onClick={() => setShowAuthDialog(true)}
                >
                  Sign In with Email
                </Button>
              </>
            )}
          </AuthCard>
        </Container>
      </HeroSection>
      
      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 3, 
          mt: 'auto',
          textAlign: 'center',
          borderTop: '1px solid #333'
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Please feel free to request demo account to nyanlinthu.dota2@gmail.com
        </Typography>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Dota 2 Draft Planner/Simulator
        </Typography>
      </Box>
      
      {/* Auth Dialog */}
      <Dialog 
        open={showAuthDialog} 
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Sign In
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleEmailAuth} variant="contained" color="primary">
            Sign In
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default LandingPage; 